import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Theme from "./Theme.js";
import Label from "./Label.js";
import IdGen from "./IdGen.js";
import S from './Checkbox.css';
import Text from "./Text.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function EMPTY() {}
class Checkbox extends React.Component {
  static contextType = Theme.Context;

  /* eslint-disable react/require-default-props,react/no-unused-prop-types */
  static propTypes = {
    /**
     * The value to be bound to the checkbox input's value attribute
     */
    value: PropTypes.bool,
    /**
     * Whether the checkbox should be disabled 
     */
    disabled: PropTypes.bool,
    /**
     * Theme variant name to be added to the component
     */
    variant: PropTypes.string,
    /**
     * CSS class to be applied to this component 
     */
    className: PropTypes.string,
    /**
     * Data to be passed through as the data key to the onChange function 
     */
    eventData: PropTypes.any,
    /**
     * HTML tabindex property to be bound to the checkbot input
     */
    tabIndex: PropTypes.any,
    /**
     * Function to be called when the checkbox's value is changed
     */
    onChange: PropTypes.func,
    /**
     * Whether event.stopPropagation() should be called on onChange events
     */
    stopPropagation: PropTypes.bool,
    /**
     * Label to be applied to the checkbox
     */
    label: PropTypes.node,
    /**
     * Icon font class or image url to be added to the checkbox when its value is true
     */
    iconOn: PropTypes.string,
    /**
     * Icon font class or image url to be added to the checkbox when its value is false
     */
    iconOff: PropTypes.string,
    /**
     * Help text to be displayed below the checkbox's label
     */
    help: PropTypes.node,
    /**
     * Theme variant to be applied to the help Text component
     */
    helpVariant: PropTypes.string
  };
  /* eslint-enable */

  static defaultProps = {
    variant: null,
    className: null,
    onChange: null,
    eventData: null,
    value: false,
    disabled: false
  };
  constructor(props) {
    super(props);
    this.id = IdGen.createId('cb');
  }

  /** 
   * (Internal) onClick of the element, adds the eventData prop to the event, adds a value to the event, and then calls the onChange prop
   */
  onClick = e => {
    const {
      onChange,
      eventData,
      disabled,
      value,
      stopPropagation
    } = this.props;
    if (stopPropagation !== false) {
      e.stopPropagation();
    }
    if (disabled) {
      return;
    }
    if (onChange) {
      this.input.focus();
      onChange({
        value: !value,
        data: eventData,
        sourceEvent: e
      });
    }
  };
  render() {
    const theme = this.context;
    const {
      className,
      containerClassName,
      labelClassName,
      variant,
      iconOn = 'fa fa-check',
      iconOff,
      onChange,
      tabIndex,
      label,
      labelVariant,
      labelSpace,
      eventData,
      value,
      id,
      layoutOptions,
      disabled = false,
      help,
      helpVariant,
      helpClassName,
      'data-testid': dataTestId,
      ...rest
    } = theme.props('Checkbox', this.props); // eslint-disable-line no-unused-vars

    const hasLabel = label != null;
    const cn = theme.classes('Checkbox', 'checkbox', variant, className, S.checkbox);
    const labelClass = theme.subclasses('RadioButton', 'label', labelClassName, S.label);
    const containerClass = theme.subclasses('RadioButton', 'container', containerClassName, S.labelHolder);
    const helpClass = theme.subclasses('RadioButton', 'help', helpClassName);
    const ap = theme.subclasses('Checkbox', 'appearance', S.appearance);
    const mn = theme.subclasses('Checkbox', 'icon', S.icon);
    const disableValue = disabled === true;
    const calcId = id || (hasLabel ? this.id : null);
    const check = /*#__PURE__*/_jsxs("div", {
      className: cn,
      onClick: this.onClick,
      "data-value": value,
      disabled: disableValue,
      "data-testid": dataTestId,
      children: [/*#__PURE__*/_jsx("input", {
        ref: e => {
          this.input = e;
        },
        type: "checkbox",
        checked: value,
        id: calcId,
        tabIndex: tabIndex,
        onChange: EMPTY
      }), /*#__PURE__*/_jsx("div", {
        className: ap,
        children: /*#__PURE__*/_jsx("i", {
          className: classnames(value ? iconOn : iconOff, mn)
        })
      })]
    });
    if (label != null) {
      const style = {
        '--count-gridNumRows': help ? 2 : 1
      };
      if (labelSpace != null) {
        style['--size-checkboxLabelSpace'] = labelSpace[0] === '-' ? `var(${labelSpace})` : labelSpace;
      }
      let lv = [];
      if (labelVariant) {
        lv.push(labelVariant);
      }
      if (variant) {
        lv.push(variant);
      }
      lv = lv.length === 0 ? null : lv.join('|');
      const labelComp = /*#__PURE__*/_jsx(Label, {
        variant: lv,
        htmlFor: calcId,
        className: labelClass,
        disabled: disabled,
        children: label
      });
      const helpComp = typeof help === 'string' ? /*#__PURE__*/_jsx(Text, {
        variant: helpVariant,
        className: helpClass,
        children: help
      }) : help || /*#__PURE__*/_jsx("div", {});
      return /*#__PURE__*/_jsxs("div", {
        className: containerClass,
        style: style,
        children: [check, " ", labelComp, /*#__PURE__*/_jsx("div", {}), " ", helpComp]
      });
    }
    return check;
  }
}
export default Checkbox;